/*
 * "Load more" and blog filtering for the editorial sections.
 *
 * Load more: the section renders extra tiles with the hidden attribute. The
 * archive button reveals the next batch on each press and, once nothing is
 * left, behaves as the plain link it is.
 *
 * Filtering: when the section lists blogs in data-circles-blog-filter, links
 * on the page to those blogs (/blogs/<handle>) swap the tiles for that blog's
 * articles, fetched from the blog's "tiles" template a page at a time. The
 * archive button then loads that blog's next page. Pressing the active link
 * again brings the original tiles back.
 *
 * Without this script every button and link simply navigates.
 */
const setUp = (layout) => {
  const section = layout.closest('section');
  const button = section?.querySelector('[data-circles-load-more-button]');
  const tiles = layout.querySelector('.news-grid, .news-masonry');
  if (!section || !tiles) return;

  const subtitle = section.querySelector('[data-circles-subtitle]');
  const title = section.querySelector('[data-circles-title]');
  const step = Number(layout.getAttribute('data-circles-load-more')) || 6;
  const original = {
    nodes: Array.from(tiles.children, (node) => node.cloneNode(true)),
    subtitle: subtitle?.textContent ?? '',
    title: title?.textContent ?? '',
    href: button?.getAttribute('href') ?? '',
  };

  /** @type {{ path: string, page: number, hasNext: boolean, link: HTMLAnchorElement } | null} */
  let filter = null;
  let busy = false;

  // The tiles template is this theme's own markup on this store's origin.
  const fetchPage = async (path, page) => {
    const response = await fetch(`${path}?view=tiles&page=${page}`, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const wrapper = doc.querySelector('[data-circles-tiles]');
    if (!wrapper) throw new Error('No tiles in response');
    return {
      nodes: Array.from(wrapper.children, (node) => document.importNode(node, true)),
      hasNext: wrapper.getAttribute('data-has-next') === 'true',
    };
  };

  const setBusy = (value) => {
    busy = value;
    tiles.setAttribute('aria-busy', String(value));
    button?.classList.toggle('disabled', value);
  };

  // Links to the filterable blogs, wherever they sit on the page.
  const handles = (layout.getAttribute('data-circles-blog-filter') ?? '')
    .split(',')
    .map((handle) => handle.trim())
    .filter(Boolean);
  const filterLinks = Array.from(document.querySelectorAll('a[href*="/blogs/"]')).filter(
    (link) =>
      link instanceof HTMLAnchorElement &&
      !section.contains(link) &&
      handles.some((handle) => link.pathname.replace(/\/$/, '').endsWith(`/blogs/${handle}`))
  );

  const markActive = (activeLink) => {
    for (const link of filterLinks) {
      const isActive = link === activeLink;
      link.classList.toggle('is-active', isActive);
      if (isActive) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  };

  const reset = () => {
    filter = null;
    tiles.replaceChildren(...original.nodes.map((node) => node.cloneNode(true)));
    if (subtitle) subtitle.textContent = original.subtitle;
    if (title) title.textContent = original.title;
    if (button) button.setAttribute('href', original.href);
    markActive(null);
  };

  const applyFilter = async (link) => {
    if (busy) return;
    setBusy(true);
    try {
      const { nodes, hasNext } = await fetchPage(link.pathname, 1);
      filter = { path: link.pathname, page: 1, hasNext, link };
      tiles.replaceChildren(...nodes);
      // The heading takes the category's English name, the line above it the
      // Japanese one.
      const en = link.querySelector('.webshop-index__promo-en')?.textContent.trim();
      const ja = link.querySelector('.webshop-index__promo-ja')?.textContent.trim();
      if (title) title.textContent = en || original.title;
      if (subtitle) subtitle.textContent = ja || original.subtitle;
      if (button) button.setAttribute('href', link.pathname);
      markActive(link);
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {
      window.location.href = link.href;
    } finally {
      setBusy(false);
    }
  };

  for (const link of filterLinks) {
    link.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      if (filter?.link === link) reset();
      else applyFilter(link);
    });
  }

  button?.addEventListener('click', async (event) => {
    if (filter) {
      if (!filter.hasNext) return;
      event.preventDefault();
      if (busy) return;
      setBusy(true);
      try {
        const { nodes, hasNext } = await fetchPage(filter.path, filter.page + 1);
        filter.page += 1;
        filter.hasNext = hasNext;
        tiles.append(...nodes);
        nodes[0]?.querySelector('a')?.focus({ preventScroll: true });
      } catch {
        window.location.href = button.href;
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!layout.hasAttribute('data-circles-load-more')) return;
    const pending = tiles.querySelectorAll('.news-tile[hidden]');
    if (pending.length === 0) return;

    event.preventDefault();
    const batch = Array.from(pending).slice(0, step);
    for (const tile of batch) tile.hidden = false;

    // Keyboard and screen-reader users land on the first new article.
    batch[0]?.querySelector('a')?.focus({ preventScroll: true });
  });
};

// Every section using this script includes it, so it can run more than once.
for (const layout of document.querySelectorAll('[data-circles-load-more], [data-circles-blog-filter]')) {
  if (layout instanceof HTMLElement && !layout.dataset.circlesBound) {
    layout.dataset.circlesBound = 'true';
    setUp(layout);
  }
}
